import React, { useEffect, useState, useCallback } from "react";
import { Modal, Upload, Button, message, Popconfirm, Space, Typography, Spin, Divider } from "antd";
import { InboxOutlined, DownloadOutlined, EyeOutlined, DeleteOutlined, FilePdfOutlined, SwapOutlined, FileProtectOutlined } from "@ant-design/icons";
import { useSelector } from "react-redux";
import {
    createSoporte,
    updateSoporte,
    getSoporte,
    deleteSoporte,
    downloadSoporte,
    viewSoporte,
} from "../../services/plaOperativo/factibilidadSoporteService";
import "./FactibilidadSoporteModal.css";

const { Text, Title } = Typography;

const FactibilidadSoporteModal = ({ open, onClose, project, permissionInsert, permissionUpdate, permissionDelete, onChange }) => {
    const [soporte, setSoporte] = useState(null);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);

    const usuario = useSelector((state) => state.auth.user?.username);

    const fetchSoporte = useCallback(async () => {
        if (!project?.oid) return;
        setLoading(true);
        try {
            const { status, data } = await getSoporte(project.oid);
            setSoporte(status === 200 ? data : null);
        } finally {
            setLoading(false);
        }
    }, [project]);

    useEffect(() => {
        if (open) {
            fetchSoporte();
        }
    }, [open, fetchSoporte]);

    const notificarCambio = () => {
        if (onChange) onChange(project);
    };

    const subirArchivo = async (file, esReemplazo) => {
        if (!usuario) {
            message.error("No se pudo identificar el usuario de la sesión.");
            return;
        }
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            formData.append("oidProyecto", project.oid);
            formData.append("usuario", usuario);

            const { status, message: msg } = esReemplazo
                ? await updateSoporte(project.oid, formData)
                : await createSoporte(formData);

            if (status === 200) {
                message.success(esReemplazo ? "Soporte reemplazado correctamente" : "Soporte cargado correctamente");
                await fetchSoporte();
                notificarCambio();
            } else {
                message.error(msg || "No se pudo guardar el archivo");
            }
        } finally {
            setUploading(false);
        }
    };

    const beforeUpload = (file) => {
        const esPdf = file.type === "application/pdf";
        if (!esPdf) {
            message.error("Solo se permiten archivos PDF");
            return Upload.LIST_IGNORE;
        }
        return true;
    };

    const handleDownload = async () => {
        const { status, message: msg } = await downloadSoporte(project.oid, soporte?.nombre);
        if (status !== 200) message.error(msg || "No se pudo descargar el archivo");
    };

    const handleView = async () => {
        const { status, message: msg } = await viewSoporte(project.oid);
        if (status !== 200) message.error(msg || "No se pudo abrir el archivo");
    };

    const handleDelete = async () => {
        const { status, message: msg } = await deleteSoporte(project.oid, usuario);
        if (status === 200) {
            message.success("Soporte eliminado correctamente");
            await fetchSoporte();
            notificarCambio();
        } else {
            message.error(msg || "No se pudo eliminar el archivo");
        }
    };

    const fechaFormateada = soporte?.fecha_creacion
        ? new Date(soporte.fecha_creacion).toLocaleString("es-CO", {
              day: "2-digit",
              month: "long",
              year: "numeric",
              hour: "2-digit",
              minute: "2-digit",
          })
        : "";

    return (
        <Modal
            title={
                <Space size={10}>
                    <FileProtectOutlined style={{ color: "#1677ff" }} />
                    <span>Factibilidad Actualizada PDF</span>
                </Space>
            }
            open={open}
            onCancel={onClose}
            footer={null}
            destroyOnHidden
            width={460}
            centered
            rootClassName="factibilidad-soporte-modal"
        >
            <div style={{ textAlign: "center", marginBottom: 20 }}>
                <Text type="secondary">
                    {project?.codigo} · {project?.proyecto}
                </Text>
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: "40px 0" }}>
                    <Spin />
                    <div style={{ marginTop: 12 }}>
                        <Text type="secondary">Consultando soporte…</Text>
                    </div>
                </div>
            ) : soporte ? (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <div
                        style={{
                            width: "100%",
                            border: "1px solid #f0f0f0",
                            borderRadius: 12,
                            padding: "24px 16px",
                            textAlign: "center",
                            background: "#fafafa",
                        }}
                    >
                        <FilePdfOutlined style={{ fontSize: 44, color: "#d4380d" }} />
                        <Title level={5} style={{ margin: "12px 0 4px", wordBreak: "break-word" }}>
                            {soporte.nombre}
                        </Title>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                            Subido por <strong>{soporte.usuario}</strong>
                            <br />
                            {fechaFormateada}
                        </Text>
                    </div>

                    <Divider style={{ margin: "20px 0 16px" }} />

                    <Space style={{ width: "100%", justifyContent: "center" }} wrap>
                        <Button type="primary" icon={<DownloadOutlined />} onClick={handleDownload}>
                            Descargar
                        </Button>
                        <Button icon={<EyeOutlined />} onClick={handleView}>
                            Ver
                        </Button>
                        {permissionUpdate && (
                            <Upload
                                accept="application/pdf"
                                showUploadList={false}
                                beforeUpload={beforeUpload}
                                customRequest={({ file }) => subirArchivo(file, true)}
                            >
                                <Button icon={<SwapOutlined />} loading={uploading}>
                                    Reemplazar
                                </Button>
                            </Upload>
                        )}
                        {permissionDelete && (
                            <Popconfirm
                                title="¿Eliminar el soporte de Factibilidad Actualizada?"
                                description="Esta acción no se puede deshacer."
                                okText="Eliminar"
                                cancelText="Cancelar"
                                okButtonProps={{ danger: true }}
                                onConfirm={handleDelete}
                            >
                                <Button icon={<DeleteOutlined />} danger type="text">
                                    Eliminar
                                </Button>
                            </Popconfirm>
                        )}
                    </Space>
                </div>
            ) : permissionInsert ? (
                <Upload.Dragger
                    accept="application/pdf"
                    showUploadList={false}
                    beforeUpload={beforeUpload}
                    customRequest={({ file }) => subirArchivo(file, false)}
                    disabled={uploading}
                    style={{ padding: "8px 0", borderRadius: 12 }}
                >
                    <p className="ant-upload-drag-icon" style={{ marginBottom: 4 }}>
                        {uploading ? <Spin /> : <InboxOutlined style={{ color: "#1677ff" }} />}
                    </p>
                    <p className="ant-upload-text" style={{ fontSize: 15, marginBottom: 2 }}>
                        {uploading ? "Subiendo archivo…" : "Arrastra el PDF aquí o haz clic para seleccionarlo"}
                    </p>
                    <p className="ant-upload-hint" style={{ padding: "0 24px" }}>
                        Este proyecto aún no tiene un soporte de Factibilidad Actualizada cargado. Solo se aceptan archivos PDF.
                    </p>
                </Upload.Dragger>
            ) : (
                <div style={{ textAlign: "center", padding: "32px 8px" }}>
                    <FilePdfOutlined style={{ fontSize: 36, color: "#bfbfbf" }} />
                    <div style={{ marginTop: 12 }}>
                        <Text type="secondary">
                            Este proyecto aún no tiene un soporte de Factibilidad Actualizada cargado.
                        </Text>
                    </div>
                </div>
            )}
        </Modal>
    );
};

export default FactibilidadSoporteModal;
